import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { BookmarkIcon, BookmarkPlusIcon, XIcon } from 'lucide-react';

import { useT } from '@/lib/i18n';
import { notify } from '@/lib/notify';
import { readSavedFilters, writeSavedFilters, type SavedFilter } from '@/lib/url-state';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/**
 * Save the view you are looking at, and come back to it (`plan-beta` B5).
 *
 * It saves the **URL**, not a bag of filter fields — which is only possible
 * because `useUrlState` put the filters there. That is what keeps this component
 * ignorant of what any given list filters on: Questions has four filters and
 * Candidates has one, and neither had to tell this anything.
 *
 * Scoped by pathname, so the Questions list does not offer a Candidates filter.
 * A saved filter is a shortcut to a *view*, and a view belongs to a screen.
 */
export function SavedFilters() {
  const t = useT();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const [filters, setFilters] = useState<SavedFilter[]>(() => readSavedFilters());
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');

  const mine = filters.filter((f) => f.path === pathname);
  const query = search.replace(/^\?/, '');

  const persist = (next: SavedFilter[]) => {
    setFilters(next);
    writeSavedFilters(next);
  };

  const save = () => {
    const trimmed = name.trim();
    if (!trimmed) return;

    // Same name on the same screen replaces, rather than accumulating three
    // "certified" filters that differ invisibly.
    const next = [
      ...filters.filter((f) => !(f.path === pathname && f.name === trimmed)),
      { name: trimmed, query, path: pathname },
    ];
    persist(next);
    setName('');
    setNaming(false);
    notify.success(t('filters.saved', { name: trimmed }));
  };

  return (
    <div className="flex items-center gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="outline">
            <BookmarkIcon className="size-4" />
            {t('filters.saved.label')}
            {mine.length > 0 && (
              <span className="text-muted-foreground ms-1 text-xs">({mine.length})</span>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel className="text-xs font-normal">
            {t('filters.thisScreen')}
          </DropdownMenuLabel>

          {mine.length === 0 ? (
            <p className="text-muted-foreground px-2 py-3 text-xs text-balance">
              {t('filters.none')}
            </p>
          ) : (
            mine.map((f) => (
              <DropdownMenuItem
                key={f.name}
                className="flex items-center justify-between gap-2"
                onSelect={() => navigate(`${f.path}${f.query ? `?${f.query}` : ''}`)}
              >
                <span className="truncate">{f.name}</span>
                <button
                  type="button"
                  aria-label={t('filters.remove', { name: f.name })}
                  className="text-muted-foreground hover:text-destructive shrink-0"
                  onClick={(e) => {
                    // The row's own onSelect would navigate to the filter being
                    // deleted, which is the one place it should not go.
                    e.stopPropagation();
                    persist(filters.filter((x) => !(x.path === f.path && x.name === f.name)));
                  }}
                >
                  <XIcon className="size-3.5" />
                </button>
              </DropdownMenuItem>
            ))
          )}

          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setNaming(true); }}>
            <BookmarkPlusIcon className="size-4" />
            {t('filters.saveCurrent')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {naming && (
        <form
          className="flex items-center gap-1"
          onSubmit={(e) => { e.preventDefault(); save(); }}
        >
          <Input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('filters.namePlaceholder')}
            aria-label={t('filters.name')}
            className="h-8 w-44"
          />
          <Button size="sm" type="submit" disabled={!name.trim()}>{t('filters.save')}</Button>
          <Button size="sm" variant="ghost" type="button" onClick={() => setNaming(false)}>
            {t('common.cancel')}
          </Button>
        </form>
      )}
    </div>
  );
}
